import { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

export default function useProfileDrafts(role, open, value) {
  const { admin } = useOutletContext() || {};
  const key = admin?.id ? `kalamanch:admin:${admin.id}:${role}:drafts` : '';
  const [drafts, setDrafts] = useState([]);
  const [error, setError] = useState('');
  const active = useRef('');
  const storedRows = useRef([]);
  const loadedKey = useRef('');
  useEffect(() => {
    loadedKey.current = '';
    if (!key) return;
    try {
      const rows = JSON.parse(localStorage.getItem(key) || '[]');
      if (!Array.isArray(rows)) throw new Error('Invalid drafts');
      if (role === 'artist') {
        const legacyKey = 'kalamanch_admin_artist_profile_draft';
        const legacy = JSON.parse(localStorage.getItem(legacyKey) || 'null');
        if (legacy?.formData) {
          const { login_password, password, token, ...formData } = legacy.formData;
          rows.push({ id: 'legacy-artist-draft', formData, editingId: legacy.editingArtistId || '',
            leadId: legacy.draftLeadId || '', savedAt: legacy.savedAt || new Date().toISOString() });
          localStorage.setItem(key, JSON.stringify(rows));
          localStorage.removeItem(legacyKey);
        }
      }
      storedRows.current = rows;
      setDrafts(rows);
      loadedKey.current = key;
    } catch {
      setError('Saved drafts could not be read. Keep this page open to protect your work.');
    }
  }, [key, role]);
  const write = (rows) => {
    try {
      localStorage.setItem(key, JSON.stringify(rows));
      storedRows.current = rows;
      setDrafts(rows);
      setError('');
      return true;
    } catch {
      setError('Draft could not be saved on this browser. Keep this page open and try again.');
      return false;
    }
  };
  const save = () => {
    if (!key || loadedKey.current !== key) { setError('Draft storage is not ready. Keep the form open and try again.'); return false; }
    if (!active.current) active.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const { login_password, password, token, ...safeForm } = value.formData;
    const draft = { ...value, formData: safeForm, id: active.current, savedAt: new Date().toISOString() };
    return write([draft, ...storedRows.current.filter((row) => row.id !== draft.id)]);
  };
  useEffect(() => {
    if (open) save();
    // Save each committed form change; do not debounce across refresh/navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key, value.formData, value.editingId, value.leadId, value.brief, value.conversionSource]);
  return {
    drafts, error, save,
    start: () => { active.current = ''; },
    restore: (draft) => { active.current = draft.id; return draft; },
    remove: (id) => write(storedRows.current.filter((row) => row.id !== id)),
    complete: () => { write(storedRows.current.filter((row) => row.id !== active.current)); active.current = ''; },
  };
}

export function ProfileDraftShelf({ store, onRestore, open }) {
  return <section aria-label="Saved profile drafts" className="rounded-2xl border border-purple-200 bg-purple-50 p-4">
    <h2 className="font-bold text-purple-900">Saved drafts ({store.drafts.length})</h2>
    <p className="text-sm text-gray-600">{open ? 'Changes save automatically. ' : ''}Cancel and Close keep your drafts. Reopen them here using Continue draft. Drafts stay in this browser after refreshing or signing in again. Passwords are not saved.</p>
    {store.error && <p role="alert" className="text-red-700">{store.error}</p>}
    {!open && store.drafts.map((draft) => <div key={draft.id} className="mt-3 flex flex-wrap items-center gap-3 rounded-xl bg-white p-3">
      <span className="flex-1">{draft.formData.name || draft.formData.organization_name || 'Untitled profile'} <small className="block text-gray-500">{new Date(draft.savedAt).toLocaleString()}</small></span>
      <button type="button" className="font-semibold text-purple-800" onClick={() => onRestore(store.restore(draft))}>Continue draft</button>
      <button type="button" className="text-gray-600" onClick={() => { if (window.confirm('Delete this saved draft?')) store.remove(draft.id); }}>Delete draft</button>
    </div>)}
  </section>;
}
